import { cmsLinkedDocument, cmsLinkedPage } from '@shared/cms/links';
import type { SiteCmsCompetitionCard, SiteCmsDraftResponse, SiteCmsImageResponse } from '@shared/types/entities';

/** Accent-insensitive search; never changes the saved value. */
export function cmsSearchKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr')
    .trim();
}
export function cmsSearchMatches(value: string, query: string): boolean {
  const haystack = cmsSearchKey(value);
  return cmsSearchKey(query)
    .split(/\s+/)
    .filter(Boolean)
    .every(word => haystack.includes(word));
}
export type CmsLinkKind = 'page' | 'pdf' | 'competition' | 'external';
export function cmsLinkKind(href: string, competitions: readonly SiteCmsCompetitionCard[]): CmsLinkKind {
  if (cmsLinkedDocument(href)) return 'pdf';
  if (cmsLinkedPage(href)) return 'page';
  if (competitions.some(card => card.href === href)) return 'competition';
  return 'external';
}
/** Match a page/document ignoring its query/fragment, without deleting those on edit. */
export function cmsSameDestination(kind: CmsLinkKind, value: string, option: string): boolean {
  if (kind === 'page') return cmsLinkedPage(value) !== null && cmsLinkedPage(value) === cmsLinkedPage(option);
  if (kind === 'pdf')
    return cmsLinkedDocument(value) !== null && cmsLinkedDocument(value) === cmsLinkedDocument(option);
  return value === option;
}
export function cmsImageFileIssue(file: Pick<File, 'name' | 'size' | 'type'> | null, count: number): string | null {
  if (!file) return null;
  if (count >= 100) return 'La bibliothèque a atteint sa limite de 100 images.';
  if (!file.size) return 'Ce fichier est vide. Choisissez une autre image.';
  if (file.size > 10 * 1024 * 1024) return 'Cette image dépasse 10 Mo. Réduisez sa taille avant de l’importer.';
  if (
    (file.type && !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) ||
    (!file.type && !/\.(jpe?g|png|webp)$/i.test(file.name))
  )
    return 'Format non pris en charge : choisissez un JPEG, PNG ou WebP.';
  return null; // Decoding, animation and pixel limits are still checked by the server.
}
export function cmsInsertUploadedImage(
  rows: readonly SiteCmsImageResponse[] | undefined,
  image: SiteCmsImageResponse,
): SiteCmsImageResponse[] {
  return [image, ...(rows ?? []).filter(row => row.id !== image.id)];
}
export function cmsSameOwner(a: SiteCmsDraftResponse, b: SiteCmsDraftResponse): boolean {
  return a.ownerId === b.ownerId && a.ownerType === b.ownerType;
}
export function cmsContentKey(draft: SiteCmsDraftResponse): string {
  return JSON.stringify([draft.title, draft.description, draft.theme, draft.branding, draft.pages, draft.headerMenu]);
}
/** A saved response is not permission to leave if another edit happened meanwhile. */
export function cmsCanLeaveAfterSave(start: number, current: number, pendingForms: number, busy: boolean): boolean {
  return start === current && pendingForms === 0 && !busy;
}

/** Same safe URL family accepted by the draft API; no validation is bypassed. */
export function cmsIsSafeLink(value: string): boolean {
  if (!value || /\s|\\/.test(value)) return false;
  if (value.startsWith('/') && !value.startsWith('//')) {
    return !value.includes('%') && !value.split(/[/?#]/).some(segment => segment === '.' || segment === '..');
  }
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch {
    return false;
  }
}
